import React from "react";
import {
  Box,
  Button,
  Center,
  Flex,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { SortingParams_SortField, sortingParams_SortFieldToJSON } from "build-ts/interface";
import { AppIcons } from "constants/icons";
import { HeaderWithStats } from "services/MetadataServices";
import { getSortByLabel } from "utils/analysisUtils";
import { listPanelScrollSx } from "utils/listScrollAreaStyles";
import { epochConverterWithTimeZone } from "utils/dateUtils";
import SplitView from "modules/common/SplitView";
import AnalystMemoryFactsContainer from "modules/analyst/containers/AnalystMemoryFacts";

export interface MemoryConfigurationPanelProps {
  analystHeaders: HeaderWithStats[];
  isLoading: boolean;
  selectedAnalystId: string | null;
  searchTerm: string;
  sortBy: SortingParams_SortField;
  sortAscending: boolean;
  defaultTimeZone: string;
  onSelectAnalyst: (id: string) => void;
  onSearchChange: (term: string) => void;
  onSortChange: (sortBy: SortingParams_SortField, ascending: boolean) => void;
}

// ---------------------------------------------------------------------------
// Single list row — robot icon + name + last modified, no action buttons
// ---------------------------------------------------------------------------

const AnalystListRow: React.FC<{
  header: HeaderWithStats;
  selected: boolean;
  onSelect: () => void;
  timeZone: string;
}> = ({ header, selected, onSelect, timeZone }) => (
  <Box
    role="button"
    tabIndex={0}
    onClick={onSelect}
    onKeyDown={e => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onSelect();
      }
    }}
    borderWidth="1px"
    borderRadius="lg"
    borderColor={selected ? "purple.400" : "gray.200"}
    bg={selected ? "purple.50" : "white"}
    px={3}
    py={2.5}
    cursor="pointer"
    _hover={{ borderColor: "purple.400", boxShadow: "sm" }}
    aria-selected={selected}
  >
    <HStack align="start" spacing={2}>
      <Icon as={AppIcons.analyst} boxSize={5} color="purple.600" mt={0.5} flexShrink={0} />
      <Box minW={0} flex={1}>
        <Text fontSize="sm" fontWeight="700" color="gray.800" mb={0.5} noOfLines={2} textAlign="left">
          {header.name || "Unnamed Analyst"}
        </Text>
        <Text fontSize="xs" color="gray.500" mb={0} textAlign="left">
          Last Modified:{" "}
          {header.last_modified_date_ms
            ? epochConverterWithTimeZone(header.last_modified_date_ms, "dd-MMM-yyyy, hh:mm a", timeZone)
            : "—"}
        </Text>
      </Box>
    </HStack>
  </Box>
);

// ---------------------------------------------------------------------------
// MemoryConfigurationPanel
// ---------------------------------------------------------------------------

const MemoryConfigurationPanel: React.FC<MemoryConfigurationPanelProps> = ({
  analystHeaders,
  isLoading,
  selectedAnalystId,
  searchTerm,
  sortBy,
  sortAscending,
  onSelectAnalyst,
  onSearchChange,
  onSortChange,
  defaultTimeZone,
}) => {
  const leftPanel = (
    <Flex direction="column" h="full" bg="white" overflow="hidden">
      {/* Search + sort header */}
      <Box px={3} pt={3} pb={2} flexShrink={0} borderBottomWidth="1px" borderColor="gray.200" bg="white">
        <Text
          fontSize="xs"
          fontWeight="700"
          color="gray.500"
          textTransform="uppercase"
          letterSpacing="wider"
          mb={2}
          textAlign="left"
        >
          Analysts
        </Text>
        <InputGroup size="sm" mb={2}>
          <InputLeftElement pointerEvents="none">
            <Icon as={AppIcons.search} color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Search analysts..."
            value={searchTerm}
            onChange={e => onSearchChange(e.target.value)}
            bg="white"
            borderColor="gray.200"
          />
        </InputGroup>
        <Flex align="center" gap={2}>
          <Text fontSize="xs" color="gray.600" mb={0} whiteSpace="nowrap">
            Sort by:
          </Text>
          <Menu>
            <MenuButton
              as={Button}
              size="xs"
              rightIcon={<Icon as={AppIcons.chevronDown} boxSize={3} />}
              variant="outline"
              borderColor="gray.300"
            >
              {getSortByLabel(sortingParams_SortFieldToJSON(sortBy))} {sortAscending ? "Asc" : "Desc"}
            </MenuButton>
            <MenuList fontSize="sm">
              <MenuItem onClick={() => onSortChange(SortingParams_SortField.LAST_MODIFIED, false)}>
                Last Modified (Desc)
              </MenuItem>
              <MenuItem onClick={() => onSortChange(SortingParams_SortField.LAST_MODIFIED, true)}>
                Last Modified (Asc)
              </MenuItem>
              <MenuItem onClick={() => onSortChange(SortingParams_SortField.CREATED, false)}>Created (Desc)</MenuItem>
              <MenuItem onClick={() => onSortChange(SortingParams_SortField.CREATED, true)}>Created (Asc)</MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      </Box>

      {/* List */}
      <Box flex={1} minH={0} overflowY="auto" px={3} py={3} sx={listPanelScrollSx(true)}>
        {isLoading ? (
          <Center py={10}>
            <Spinner color="purple.500" />
          </Center>
        ) : analystHeaders.length === 0 ? (
          <Text fontSize="sm" color="gray.500" textAlign="left">
            No analysts found.
          </Text>
        ) : (
          <VStack align="stretch" spacing={2}>
            {analystHeaders.map(h => (
              <AnalystListRow
                key={h.id}
                header={h}
                selected={selectedAnalystId === h.id}
                onSelect={() => onSelectAnalyst(h.id!)}
                timeZone={defaultTimeZone}
              />
            ))}
          </VStack>
        )}
      </Box>
    </Flex>
  );

  const rightPanel = selectedAnalystId ? (
    <AnalystMemoryFactsContainer analystId={selectedAnalystId} isOpen />
  ) : (
    <Center h="full" flexDirection="column" gap={3} color="gray.400">
      <Icon as={AppIcons.analyst} boxSize={10} />
      <Text fontSize="sm" mb={0} textAlign="center">
        Select an analyst to see its memory.
      </Text>
    </Center>
  );

  return <SplitView leftPanel={leftPanel} rightPanel={rightPanel} isRightPanelVisible defaultRightWidth={42} />;
};

export default MemoryConfigurationPanel;
